import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GUIDES_DATA } from '../data/guidesData';
import ListingService from '../services/ListingService';

const GuideDetailPage = ({ agent }) => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();

  const article = GUIDES_DATA.find((g) => g.slug === articleSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!article) {
      document.title = "Guide Not Found | Nicole Pua Property Agent";
      return;
    }

    document.title = `${article.title} | Nicole Pua Property Agent`;

    // Helper to set or update meta tag
    const setMetaTag = (attrName, attrVal, content) => {
      let tag = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrVal);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Meta description
    setMetaTag('name', 'description', article.summary);

    // OpenGraph meta tags
    setMetaTag('property', 'og:title', article.title);
    setMetaTag('property', 'og:description', article.summary);
    setMetaTag('property', 'og:image', article.coverImage);
    setMetaTag('property', 'og:url', `https://nicolejbproperty.com/guides/${article.slug}`);
    setMetaTag('property', 'og:type', 'article');

    // Twitter Card meta tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', article.title);
    setMetaTag('name', 'twitter:description', article.summary);
    setMetaTag('name', 'twitter:image', article.coverImage);

    // Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://nicolejbproperty.com/guides/${article.slug}`);

    // Inject Article Schema.org JSON-LD
    const schemaId = 'article-jsonld-schema';
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = schemaId;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.summary,
      "image": [article.coverImage],
      "author": {
        "@type": "Person",
        "name": "Nicole Pua",
        "jobTitle": "Registered Real Estate Negotiator"
      },
      "publisher": {
        "@type": "RealEstateAgent",
        "name": "Nicole Pua JB Property Agent",
        "url": "https://nicolejbproperty.com"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://nicolejbproperty.com/guides/${article.slug}`
      }
    };
    schemaScript.textContent = JSON.stringify(articleSchema);

    return () => {
      if (schemaScript) schemaScript.remove();
    };
  }, [articleSlug, article]);

  if (!article) {
    return (
      <div className="all-areas-page" style={{ paddingTop: '100px', textAlign: 'center', minHeight: '60vh' }}>
        <div className="area-container">
          <h2>Guide Article Not Found</h2>
          <p>The requested property guide article could not be found.</p>
          <button 
            className="btn-clear-search" 
            onClick={() => navigate('/guides')}
            style={{ marginTop: '1.5rem' }}
          >
            RETURN TO PROPERTY GUIDES
          </button>
        </div>
      </div>
    );
  }

  const whatsappMsg = `Hi Nicole, I just read your guide "${article.title}" and would like to ask a few questions regarding property in Johor Bahru.`;
  const whatsappUrl = `https://wa.me/${agent?.whatsappNumber || '60163288120'}?text=${encodeURIComponent(whatsappMsg)}`;

  const relatedArticles = GUIDES_DATA.filter((g) => g.slug !== article.slug).slice(0, 2);

  return (
    <div className="guide-detail-page">
      {/* Header Banner */}
      <div className="guide-detail-hero">
        <div className="guide-detail-hero-container">
          {/* Breadcrumbs */}
          <div className="breadcrumbs" style={{ margin: 0 }}>
            <span className="breadcrumb-item" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
              Home
            </span>
            <span className="breadcrumb-separator"><i className="bi bi-chevron-right"></i></span>
            <span className="breadcrumb-item" onClick={() => { navigate('/guides'); window.scrollTo(0, 0); }}>
              Property Guides
            </span>
            <span className="breadcrumb-separator"><i className="bi bi-chevron-right"></i></span>
            <span className="breadcrumb-item active">{article.category}</span>
          </div>

          <div className="guide-meta-pill-bar">
            <span className="guide-category-badge">{article.category}</span>
            <span className="guide-meta-text"><i className="bi bi-calendar3"></i> {article.date}</span>
            <span className="meta-divider">•</span>
            <span className="guide-meta-text"><i className="bi bi-clock"></i> {article.readTime}</span>
          </div>

          <h1 className="guide-detail-title">{article.title}</h1>
          <p className="guide-detail-lead">{article.summary}</p>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="guide-detail-container">
        <div className="guide-main-column">
          {/* Article Cover Image */}
          <div className="guide-cover-wrapper">
            <img src={article.coverImage} alt={article.title} className="guide-cover-img" />
          </div>

          {/* Key Highlights Callout Box */}
          {article.highlights && article.highlights.length > 0 && (
            <div className="guide-key-takeaways">
              <h3 className="takeaways-title">
                <i className="bi bi-lightning-charge-fill"></i> KEY TAKEAWAYS
              </h3>
              <ul className="takeaways-list">
                {article.highlights.map((item, idx) => (
                  <li key={idx}>
                    <i className="bi bi-check-circle-fill"></i> {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Formatted Article Body Sections */}
          <div className="guide-body-content">
            {article.sections && article.sections.map((section, idx) => (
              <div className="article-section-block" key={idx} id={`section-${idx}`}>
                {section.heading && <h2 className="article-section-heading">{section.heading}</h2>}

                {section.image && (
                  <div className="article-section-image-wrapper" style={{ margin: '1.25rem 0', borderRadius: '10px', overflow: 'hidden' }}>
                    <img src={section.image} alt={section.heading || 'Article section image'} className="article-section-img" style={{ width: '100%', maxHeight: '350px', objectFit: 'cover' }} />
                  </div>
                )}

                {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="article-paragraph">{p}</p>
                ))}

                {section.callout && (
                  <div className="article-callout-box">
                    <i className="bi bi-exclamation-triangle-fill"></i> {section.callout}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Split Layout: Left (Author Bio & CTA Card) | Right (More Guides) */}
          <div className="guide-bottom-split-layout">
            <div className="guide-bottom-left-col">
              {/* Author Profile Bio Box */}
              <div className="guide-author-bio-box">
                <img 
                  src={ListingService.formatImageUrl(agent?.photoUrl || "/api/app-images/profile.jpg")} 
                  alt="Nicole Pua" 
                  className="bio-avatar" 
                />
                <div className="bio-info">
                  <h4 className="bio-name">Nicole Pua (妮可地产经纪)</h4>
                  <p className="bio-text">
                    Johor Bahru Property Specialist. Providing honest advice, clear financial analysis, and personalized guidance to help home buyers, commuters, and investors make informed real estate decisions.
                  </p>
                </div>
              </div>

              {/* Article Bottom Call-To-Action */}
              <div className="guide-cta-card">
                <div className="cta-content">
                  <h3>Planning to Buy, Sell, or Rent Property in JB?</h3>
                  <p>Get direct, professional advice tailored to your requirements and budget.</p>
                </div>
                <a 
                  href={whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-hero-primary" 
                  style={{ textDecoration: 'none' }}
                >
                  <i className="bi bi-whatsapp"></i> ASK NICOLE ON WHATSAPP
                </a>
              </div>
            </div>

            {/* Right Column: More Guides Widget */}
            {relatedArticles.length > 0 && (
              <div className="guide-bottom-right-col">
                <div className="bottom-more-guides-widget">
                  <h4 className="bottom-more-guides-title">
                    <i className="bi bi-journal-text"></i> MORE GUIDES
                  </h4>
                  <div className="related-guides-list">
                    {relatedArticles.map((rel) => (
                      <div 
                        key={rel.id} 
                        className="related-guide-item"
                        onClick={() => {
                          navigate(`/guides/${rel.slug}`);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={rel.coverImage} alt={rel.title} className="related-guide-img" />
                        <div>
                          <span className="related-guide-cat">{rel.category}</span>
                          <h5 className="related-guide-title">{rel.title}</h5>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideDetailPage;
